<template>
  <div id="note-configuration">
    <div class="page-title px-4 mb-4">
      <h2>
        <span class="me-2">Note ID #{{ note.id }} - {{ note.title }}</span>
      </h2>
      <div class="d-flex align-center justify-center">
        <save-button :save-action="saveNoteConfig" />
        <delete-button class="ml-6" :delete-action="deleteNote" :type="'note'" :title="`${note.title}`"></delete-button>
      </div>
    </div>

    <v-divider class="mt-4 mb-10"></v-divider>

    <v-row v-if="is_loading" class="inline-loader">
      <v-col>
        <v-card class="mb-4">
          <v-progress-circular color="primary" indeterminate></v-progress-circular>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12" md="8" lg="8">
        <v-card class="mb-4">
          <v-card-title class="mb-0">
            <h2 class="text-left text-xl font-weight-semibold label">Note details</h2>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="7" class="mt-5">
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="notesData.title"
                      :rules="[v => !!v || 'Title is required']"
                      label="Title *"
                      outlined
                      dense
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="notesData.issue_type"
                      :items="typeItems"
                      :rules="[v => !!v || 'Type is required']"
                      label="Type *"
                      outlined
                      dense
                      required
                    ></v-select>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="notesData.location"
                      label="Location (optional)"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="notesData.priority"
                      :items="priorityItems"
                      :rules="[v => !!v || 'Priority is required']"
                      label="Priority *"
                      outlined
                      dense
                      required
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="notesData.claimant"
                      :rules="[v => !!v || 'Created By is required']"
                      label="Created By *"
                      outlined
                      dense
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row v-if="notesData.fault_category || notesData.status == 'Closed'">
                  <v-col cols="12">
                    <v-select
                      v-model="notesData.fault_category"
                      :items="faultCategoryItems"
                      label="Fault Category"
                      outlined
                      dense
                      required
                    ></v-select>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="5">
                <v-row>
                  <v-col cols="12">
                    <v-list>
                      <v-list-item dense class="px-0">
                        <v-row>
                          <v-col class="d-flex" cols="3">
                            <span class="font-weight-medium me-2">Created:</span>
                          </v-col>
                          <v-col cols="9">{{ formattedCreatedDate }}</v-col>
                        </v-row>
                      </v-list-item>
                      <v-list-item dense class="px-0">
                        <v-row>
                          <v-col class="d-flex" cols="3">
                            <span class="font-weight-medium me-2">Duration:</span>
                          </v-col>
                          <v-col cols="9">{{ formatDuration(notesData.duration_minutes) }}</v-col>
                        </v-row>
                      </v-list-item>

                      <v-divider class="my-3"></v-divider>

                      <v-list-item v-if="notesData.vehicle_id" dense class="px-0">
                        <v-row>
                          <v-col class="d-flex" cols="3">
                            <span class="font-weight-medium me-2">Vehicle:</span>
                          </v-col>
                          <v-col cols="9">
                            <div class="d-flex flex-wrap align-center">
                              <router-link
                                :to="{ name: 'next-stop-vehicle-config', params: { id: notesData.vehicle_id } }"
                                class="cursor-pointer text-decoration-none me-2"
                              >
                                <v-chip link small>{{ notesData.vehicle_id }}</v-chip>

                                <v-chip
                                  small
                                  class="me-1"
                                  outlined
                                  :color="
                                    currentVehicle.status.status === 'offline'
                                      ? 'error'
                                      : currentVehicle.status.status === 'idle'
                                      ? 'success'
                                      : 'warning'
                                  "
                                >
                                  {{ currentVehicle.status.status }}
                                </v-chip>
                              </router-link>
                              <TagChips :config="currentVehicleConfig" />
                            </div>
                          </v-col>
                        </v-row>
                      </v-list-item>

                      <v-list-item v-if="notesData.stop_id" dense class="px-0">
                        <v-row>
                          <v-col class="d-flex" cols="3">
                            <span class="font-weight-medium me-2">Stop:</span>
                          </v-col>
                          <v-col cols="9">
                            <router-link
                              :to="{ name: 'next-stop-stop-config', params: { id: notesData.stop_id } }"
                              class="cursor-pointer text-decoration-none"
                            >
                              <v-chip link small>{{ notesData.stop_id }}</v-chip>
                            </router-link>
                          </v-col>
                        </v-row>
                      </v-list-item>

                      <v-list-item v-if="notesData.route_id" dense class="px-0">
                        <v-row>
                          <v-col class="d-flex" cols="3">
                            <span class="font-weight-medium me-2">Route:</span>
                          </v-col>
                          <v-col cols="9">
                            <router-link
                              :to="{ name: 'next-stop-route-config', params: { id: notesData.route_id } }"
                              class="cursor-pointer text-decoration-none"
                            >
                              <v-chip link small>{{ notesData.route_id }}</v-chip>
                            </router-link>
                          </v-col>
                        </v-row>
                      </v-list-item>

                      <v-list-item v-if="notesData.device_id" dense class="px-0">
                        <v-row>
                          <v-col class="d-flex" cols="3">
                            <span class="font-weight-medium me-2">Device:</span>
                          </v-col>
                          <v-col cols="9">
                            <div class="d-flex flex-wrap align-center">
                              <router-link
                                :to="{ name: 'next-stop-device-config', params: { id: notesData.device_id } }"
                                class="cursor-pointer text-decoration-none me-2"
                              >
                                <v-chip link small>{{ notesData.device_id }}</v-chip>
                              </router-link>
                              <TagChips :config="currentDeviceConfig" />
                            </div>
                          </v-col>
                        </v-row>
                      </v-list-item>

                      <v-list-item v-if="notesData.case_id" dense class="px-0">
                        <v-row>
                          <v-col class="d-flex" cols="3">
                            <span class="font-weight-medium me-2">Hardware:</span>
                          </v-col>
                          <v-col cols="9">
                            <router-link
                              :to="{ name: 'next-stop-device-config', params: { id: notesData.device_id } }"
                              class="cursor-pointer text-decoration-none"
                            >
                              <v-chip link small>{{ notesData.case_id }}</v-chip>
                            </router-link>
                          </v-col>
                        </v-row>
                      </v-list-item>

                      <v-divider class="my-3"></v-divider>

                      <v-list-item dense class="px-0">
                        <v-row>
                          <v-col class="d-flex" cols="3">
                            <span class="font-weight-medium me-2">Assigned:</span>
                          </v-col>
                          <v-col cols="9">{{ notesData.assigned || "Unassigned" }}</v-col>
                        </v-row>
                      </v-list-item>

                      <v-list-item dense class="px-0">
                        <v-row>
                          <v-col class="d-flex" cols="3">
                            <span class="font-weight-medium me-2">Status:</span>
                          </v-col>
                          <v-col cols="9">{{ notesData.status }}</v-col>
                        </v-row>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-title class="mb-4">
            <h2 class="text-left text-xl font-weight-semibold label">Note History</h2>
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-timeline dense>
                  <v-timeline-item v-for="(update, index) in note.updates" :key="index" small>
                    <v-card>
                      <v-card-title class="pa-2 text-caption">
                        {{ formatUpdateTimestamp(update.created) }} - {{ update.updated_by }}
                      </v-card-title>
                      <v-card-text class="pa-2">
                        <p class="mb-3 text-pre-wrap"><strong>Comments:</strong> {{ update.comments }}</p>
                        <p class="mb-3"><strong>Status:</strong> {{ update.status }}</p>
                        <p class="mb-1"><strong>Assigned To:</strong> {{ update.assigned || "Unassigned" }}</p>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>

                  <v-timeline-item small>
                    <v-card>
                      <v-card-title class="mb-4">
                        <h2 class="text-left text-xl font-weight-semibold label">Add an Update</h2>
                      </v-card-title>
                      <v-card-text>
                        <v-row>
                          <v-col cols="12">
                            <v-textarea
                              v-model="newUpdate.comments"
                              label="Update Comments"
                              outlined
                              dense
                            ></v-textarea>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <v-select
                              v-model="newUpdate.status"
                              :items="statusItems"
                              label="Status"
                              outlined
                              dense
                            ></v-select>
                          </v-col>
                          <v-col v-if="newUpdate.status == 'Closed'" cols="12">
                            <v-select
                              v-model="notesData.fault_category"
                              outlined
                              dense
                              clearable
                              :items="faultCategoryItems"
                              label="Fault Category"
                            ></v-select>
                          </v-col>
                          <v-col cols="12">
                            <v-select
                              v-model="newUpdate.assigned"
                              outlined
                              dense
                              clearable
                              :items="assignedItems"
                              label="Assigned To"
                            ></v-select>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <v-btn color="primary" @click="addNoteUpdate">Add Update</v-btn>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card v-if="notesData.device_id && notesData.created" class="mb-4">
          <v-card-title class="mb-0">
            <h2 class="text-left text-xl font-weight-semibold label">
              Device Metrics
              <span class="text-body-2 text--secondary ms-2">
                ({{ notesData.device_id }}, ±2h around note creation)
              </span>
            </h2>
          </v-card-title>
          <v-card-text>
            <DeviceMetricsChart
              :key="`${notesData.device_id}-${notesData.created}`"
              :device-id="notesData.device_id"
              :initial-start="metricsRange.start"
              :initial-end="metricsRange.end"
              :annotations="metricsAnnotations"
              auto-load
            />
          </v-card-text>
        </v-card>

        <v-card v-if="notesData.device_id && notesData.created" class="mb-4">
          <v-card-title class="mb-0">
            <h2 class="text-left text-xl font-weight-semibold label">
              Trip Assignments
              <span class="text-body-2 text--secondary ms-2">
                ({{ notesData.device_id }}, ±2h around note creation)
              </span>
            </h2>
          </v-card-title>
          <v-card-text>
            <v-progress-circular v-if="tripAssignmentsLoading" color="primary" indeterminate></v-progress-circular>
            <v-data-table v-else :headers="tripAssignmentHeaders" :items="tripAssignments" :items-per-page="10" dense>
              <template v-slot:item.trip_id="{ item }">{{ item.trip_id || "null" }}</template>
              <template v-slot:item.action="{ item }">{{ item.action || "null" }}</template>
              <template v-slot:no-data>No trip assignments in this range.</template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-card v-if="notesData.device_id && notesData.created" class="mb-4">
          <v-card-title class="mb-0">
            <h2 class="text-left text-xl font-weight-semibold label">
              Device Error Logs
              <span class="text-body-2 text--secondary ms-2">
                ({{ notesData.device_id }}, ±2h around note creation)
              </span>
            </h2>
          </v-card-title>
          <v-card-text>
            <v-progress-circular v-if="errorLogsLoading" color="primary" indeterminate></v-progress-circular>
            <v-data-table
              v-else
              :headers="errorLogHeaders"
              :items="errorLogs"
              :items-per-page="10"
              sort-by="date_time"
              sort-desc
              dense
            >
              <template v-slot:item.message="{ item }">
                <span class="text-pre-wrap">{{ item.message }}</span>
              </template>
              <template v-slot:item.date_time="{ item }">
                <span class="text-no-wrap">{{ item.date_time }}</span>
              </template>
              <template v-slot:no-data>No error logs in this range.</template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4" lg="4">
        <v-card v-if="currentDeviceStatus" class="mb-4">
          <v-card-title class="mb-0">
            <h2 class="text-left text-xl font-weight-semibold label">Current Vehicle/Device Info</h2>
          </v-card-title>
          <v-card-text>
            <MglMap
              ref="positionMap"
              :access-token="$config.mapbox.accessToken"
              :track-resize="true"
              :touch-zoom-rotate="true"
              :scroll-zoom="true"
              :attribution-control="false"
              :map-style="$config.mapbox.style"
              :zoom="$config.mapbox.zoom"
              :center="[currentVehicle.status.position.longitude, currentVehicle.status.position.latitude]"
            >
              <MglMarker
                :coordinates="[currentVehicle.status.position.longitude, currentVehicle.status.position.latitude]"
              >
                <vehicle-marker
                  slot="marker"
                  :route="vehicleRoute"
                  :position="currentVehicle.status.position"
                  :vehicle-data="currentVehicle"
                ></vehicle-marker>
              </MglMarker>
            </MglMap>
            <v-simple-table dense class="mt-4">
              <tbody>
                <tr>
                  <td>Device</td>
                  <td>
                    <router-link
                      :to="{ name: 'next-stop-device-config', params: { id: currentDevice.device_id } }"
                      class="cursor-pointer text-decoration-none"
                    >
                      <v-chip small link>{{ currentDevice.device_id }}</v-chip>
                    </router-link>
                  </td>
                </tr>
                <tr>
                  <td>Vehicle</td>
                  <td>
                    <router-link
                      v-if="currentDevice.config && currentDevice.config.vehicle_id"
                      :to="{ name: 'next-stop-vehicle-config', params: { id: currentDevice.config.vehicle_id } }"
                      class="cursor-pointer text-decoration-none"
                    >
                      <v-chip
                        :class="currentDevice.config.vehicle_id == notesData.vehicle_id ? 'success--text' : ''"
                        small
                        link
                      >
                        {{ currentDevice.config.vehicle_id }}
                      </v-chip>
                    </router-link>
                    <span v-else class="text--secondary">No vehicle</span>
                  </td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td>{{ currentDeviceStatus.status || "Unknown" }}</td>
                </tr>
                <tr>
                  <td>Reachable</td>
                  <td>
                    {{ deviceReachableText }}
                    <span v-if="deviceReachableReason" class="text--secondary">({{ deviceReachableReason }})</span>
                  </td>
                </tr>
                <tr>
                  <td>Services Not Running</td>
                  <td>
                    <span v-if="deviceServicesNotRunning.length" class="error--text">
                      {{ deviceServicesNotRunning.join(", ") }}
                    </span>
                    <span v-else>-</span>
                  </td>
                </tr>
                <tr>
                  <td>On Trip</td>
                  <td>
                    <template v-if="deviceTripId">{{ deviceTripId }}</template>
                    <template v-else>-</template>
                  </td>
                </tr>
                <tr>
                  <td>Position Timestamp</td>
                  <td>
                    {{
                      currentDeviceStatus.position_timestamp
                        ? formatDate(currentDeviceStatus.position_timestamp, "en-NZ")
                        : "-"
                    }}
                  </td>
                </tr>
                <tr>
                  <td>Device Timestamp</td>
                  <td>
                    {{
                      currentDeviceStatus.device_timestamp
                        ? formatDate(currentDeviceStatus.device_timestamp, "en-NZ")
                        : "-"
                    }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>

        <v-row>
          <v-col cols="12">
            <h2 class="text-left text-xl font-weight-semibold label">
              Related Notes
              <v-chip v-if="filteredRelatedNotes.length" small class="ms-2">
                {{ filteredRelatedNotes.length }}
              </v-chip>
            </h2>
          </v-col>
        </v-row>

        <div v-if="filteredRelatedNotes.length">
          <v-card v-for="item in filteredRelatedNotes" :key="item.id" class="my-4">
            <v-card-text class="pa-3">
              <v-row>
                <v-col cols="12">
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th class="text-left">Note</th>
                        <th class="text-left">
                          <router-link
                            :to="{ name: 'next-stop-note-config', params: { id: item.id } }"
                            class="cursor-pointer text-decoration-none"
                          >
                            <div class="d-flex">
                              <div class="d-flex flex-column">
                                <span class="font-weight-semibold text--primary"
                                  >#{{ item.id }} - {{ item.title }} &raquo;</span
                                >
                              </div>
                            </div>
                          </router-link>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Status</td>
                        <td>
                          {{ item.status }}
                          <span v-if="item.status !== 'Closed'"
                            >({{ item.assigned ? "Assigned to " + item.assigned : "Unassigned" }})</span
                          >
                        </td>
                      </tr>
                      <tr v-if="item.vehicle_id">
                        <td>Vehicle</td>
                        <td>
                          <router-link
                            :to="{ name: 'next-stop-vehicle-config', params: { id: item.vehicle_id } }"
                            class="cursor-pointer text-decoration-none"
                          >
                            <v-chip :class="item.vehicle_id == notesData.vehicle_id ? 'success--text' : ''" small link>
                              {{ item.vehicle_id }}
                            </v-chip>
                          </router-link>
                        </td>
                      </tr>
                      <tr v-if="item.device_id">
                        <td>Device</td>
                        <td>
                          <router-link
                            :to="{ name: 'next-stop-device-config', params: { id: item.device_id } }"
                            class="cursor-pointer text-decoration-none"
                          >
                            <v-chip :class="item.device_id == notesData.device_id ? 'success--text' : ''" small link>
                              {{ item.device_id }}
                            </v-chip>
                          </router-link>
                        </td>
                      </tr>
                      <tr v-if="item.case_id">
                        <td>Hardware ID</td>
                        <td>
                          <router-link
                            :to="{ name: 'next-stop-device-config', params: { id: item.device_id } }"
                            class="cursor-pointer text-decoration-none"
                          >
                            <v-chip :class="item.case_id == notesData.case_id ? 'success--text' : ''" small link>
                              {{ item.case_id }}
                            </v-chip>
                          </router-link>
                        </td>
                      </tr>
                      <tr v-if="item.stop_id">
                        <td>Stop ID</td>
                        <td>
                          <span :class="item.stop_id == notesData.stop_id ? 'success--text' : ''">{{
                            item.stop_id
                          }}</span>
                        </td>
                      </tr>
                      <tr v-if="item.route_id">
                        <td>Route</td>
                        <td>
                          <span :class="item.route_id == notesData.route_id ? 'success--text' : ''">{{
                            item.route_id
                          }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td>Created</td>
                        <td>{{ convertCreatedDate(item) }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>
        <div v-else>
          <v-card class="my-4">
            <v-card-text>
              <span>No related Notes</span>
            </v-card-text>
          </v-card>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { getCurrentInstance } from "@vue/composition-api"
import { mapState } from "vuex"
import { MglMap, MglMarker } from "vue-mapbox"
import axios from "axios"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import "mapbox-gl/dist/mapbox-gl.css"
import SaveButton from "../components/SaveButton.vue"
import DeleteButton from "@/views/next-stop/components/DeleteButton.vue"
import DeviceMetricsChart from "@/views/next-stop/devices/components/DeviceMetricsChart.vue"
import VehicleMarker from "@/components/vehicle/Marker.vue"
import store from "@/store"
import globalHelpers from "@/mixins/globalHelpers"
import TagChips from "@/views/next-stop/components/TagChips.vue"

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: "NoteConfiguration",

  metaInfo() {
    return {
      title: `Edit Note`,
    }
  },
  components: {
    SaveButton,
    DeleteButton,
    DeviceMetricsChart,
    MglMap,
    MglMarker,
    TagChips,
    VehicleMarker,
  },

  mixins: [globalHelpers],

  data() {
    return {
      positionMapPanel: undefined,
      priorityItems: ["Low", "Medium", "High", "Urgent"],
      statusItems: ["Open", "Pending", "On Hold", "Closed"],
      typeItems: ["Not Running", "Confirmation Rate", "Observation"],
      assignedItems: ["GWRC", "Operator", "Somar", "Radiola", "Quickstart", "Other"],
      faultCategoryItems: [
        "Software/System Issue",
        "GPS/Cellular Dropout",
        "Connectivity Issue",
        "SIM Card Fault",
        "SIM Card Holder Fault",
        "Power/Isolator Issue",
        "Data/Log Gap",
        "No Fault Found / Self Recovered",
        "Hardware/System Reset",
        "Physical Damage",
        "Internal Component Failure",
        "Other",
      ],
      is_loading: true,
      notesData: {
        id: null,
        title: "",
        issue_type: "",
        stop_id: "",
        route_id: "",
        vehicle_id: "",
        device_id: "",
        case_id: "",
        comments: "",
        priority: null,
        status: null,
        claimant: "",
        assigned: "",
        location: "",
        created: null,
        fault_category: null,
        updates: [],
      },
      newUpdate: {
        comments: "",
        status: null,
        assigned: null,
        updated_by: null,
      },
      tripAssignments: [],
      tripAssignmentsLoading: false,
      tripAssignmentsRequest: axios.CancelToken.source(),
      tripAssignmentHeaders: [
        { text: "Action", value: "action", sortable: false },
        { text: "Trip ID", value: "trip_id", sortable: false },
        { text: "Date/Time", value: "date_time" },
      ],
      errorLogs: [],
      errorLogsLoading: false,
      errorLogsRequest: axios.CancelToken.source(),
      errorLogHeaders: [
        { text: "Service", value: "service" },
        { text: "Level", value: "level" },
        { text: "Message", value: "message", sortable: false },
        { text: "Date/Time", value: "date_time" },
      ],
    }
  },

  watch: {
    // Mapbox measures its container as it loads, so a map booted inside a panel
    // that is still opening comes out the wrong size. Same nudge the GPS history
    // map needs inside its dialog, timed to the expansion transition.
    positionMapPanel(panel) {
      if (panel === undefined) {
        return
      }

      setTimeout(() => this.$refs.positionMap?.map?.resize(), 300)
    },
    "$route.params.id": {
      immediate: true,
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.loadNote(newId)
        }
      },
    },
    metricsRange: {
      immediate: true,
      handler(range) {
        if (this.notesData.device_id && range && range.start && range.end) {
          this.fetchTripAssignments()
          this.fetchErrorLogs()
        }
      },
    },
    "notesData.device_id": {
      handler() {
        if (this.notesData.device_id && this.metricsRange.start && this.metricsRange.end) {
          this.fetchTripAssignments()
          this.fetchErrorLogs()
        } else {
          this.tripAssignments = []
          this.errorLogs = []
        }
      },
    },
  },

  computed: {
    ...mapState({
      note: state => state.selected_note,
      allNotes: state => state.gtfs.notes,
      devices: state => state.gtfs.devices,
      vehicles: state => state.gtfs.vehicles, // Ensure vehicles are mapped for tag lookup
      routes: state => state.gtfs.routes,
    }),
    formattedCreatedDate() {
      return this.note.created ? this.formatDate(this.note.created, "en-NZ") : "Unknown"
    },
    metricsRange() {
      if (!this.notesData.created) {
        return { start: null, end: null, createdMs: null }
      }

      const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/
      const ms = iso8601Regex.test(this.notesData.created)
        ? new Date(this.notesData.created).getTime()
        : parseInt(this.notesData.created, 10) * 1000
      const created = dayjs(ms).tz("Pacific/Auckland")

      return {
        start: created.subtract(2, "hour").format("YYYY-MM-DDTHH:mm"),
        end: created.add(2, "hour").format("YYYY-MM-DDTHH:mm"),
        createdMs: created.valueOf(),
      }
    },
    metricsAnnotations() {
      const annotations = []

      if (this.metricsRange.createdMs) {
        annotations.push({
          timestamp: this.metricsRange.createdMs,
          label: "Note created",
          color: "#424242",
          type: "note_created",
          typeLabel: "Note created",
        })
      }

      this.tripAssignments.forEach(item => {
        const ts = dayjs.tz(item.date_time, "Pacific/Auckland").valueOf()
        if (!Number.isFinite(ts)) {
          return
        }
        const isConfirmed = item.action === "confirmed"
        annotations.push({
          timestamp: ts,
          label: `${isConfirmed ? "Confirmed" : "Sent"} ${item.trip_id || "null"}`,
          color: isConfirmed ? "#2E7D32" : "#81C784",
          type: isConfirmed ? "trip_assignment_confirmed" : "trip_assignment_sent",
          typeLabel: isConfirmed ? "Trip confirmed" : "Trip sent",
        })
      })

      this.errorLogs.forEach(item => {
        const ts = dayjs.tz(item.date_time, "Pacific/Auckland").valueOf()
        if (!Number.isFinite(ts)) {
          return
        }
        annotations.push({
          timestamp: ts,
          label: `Error ${item.service || "unknown"}`,
          color: "#C62828",
          type: "error_log",
          typeLabel: "Error logs",
        })
      })

      return annotations
    },
    vehicleID() {
      return this.getVehicleId(this.note.foreign_id)
    },
    convertCreatedDate() {
      return function (item) {
        return item.created ? this.formatDate(item.created, "en-NZ") : "Unknown"
      }
    },
    currentVehicle() {
      const vehicleId = this.notesData.vehicle_id
      if (!vehicleId) {
        return []
      }

      const vehicle = this.vehicles?.find(v => v.vehicle_id === vehicleId)

      if (vehicle) {
        return vehicle
      }

      return []
    },
    currentVehicleConfig() {
      const vehicleId = this.notesData.vehicle_id
      if (!vehicleId) {
        return null
      }

      return this.vehicles?.find(v => v.vehicle_id === vehicleId)?.config || null
    },
    noteDevice() {
      const deviceId = this.notesData.device_id

      return (deviceId && this.devices?.find(d => d.device_id === deviceId)) || null
    },
    currentDeviceConfig() {
      return this.noteDevice?.config || null
    },
    // The note's own device, or failing that whichever device is fitted to the
    // note's vehicle, so a vehicle-only note still reports live device state
    currentDevice() {
      if (this.noteDevice) {
        return this.noteDevice
      }

      const vehicleId = this.notesData.vehicle_id

      return (vehicleId && this.devices?.find(d => d.config?.vehicle_id === vehicleId)) || null
    },
    currentDeviceStatus() {
      return this.currentDevice?.status || null
    },
    // A missing is_reachable is not the same as a device that failed a check,
    // so it reads as unknown rather than No
    deviceReachableText() {
      const reachable = this.currentDeviceStatus?.is_reachable?.reachable

      if (typeof reachable !== "boolean") {
        return "Unknown"
      }

      return reachable ? "Yes" : "No"
    },
    deviceReachableReason() {
      return this.currentDeviceStatus?.is_reachable?.reason || ""
    },
    deviceServicesNotRunning() {
      return this.currentDeviceStatus?.services_not_running || []
    },
    deviceTripId() {
      const tripId = this.currentDeviceStatus?.trip?.trip_id

      return tripId ? decodeURIComponent(tripId) : ""
    },
    devicePosition() {
      const position = this.currentDeviceStatus?.position

      if (!position || position.latitude == null || position.longitude == null) {
        return "-"
      }

      return `${position.latitude}, ${position.longitude}`
    },
    vehicleHasPosition() {
      return !!this.currentVehicle?.status?.position?.longitude
    },
    // Colours the marker by route when the route is known, the same as the
    // vehicle details page. Without it the marker falls back to a status colour.
    vehicleRoute() {
      const routeId = this.currentVehicle?.status?.trip?.route_id

      if (!routeId) {
        return undefined
      }

      return this.routes?.find(r => r.route_id.toString() === routeId.toString())
    },
    filteredRelatedNotes() {
      const filterKeys = ["vehicle_id", "device_id", "case_id", "stop_id", "route_id"]
      const hasFilterCriteria = filterKeys.some(key => this.notesData[key] !== "" && this.notesData[key] !== null)
      if (!hasFilterCriteria) {
        return []
      }

      return this.allNotes.filter(note => {
        if (this.notesData.id === note.id) {
          return false
        }
        let matchesCriteria = false
        matchesCriteria = filterKeys.some(key => {
          const notesDataValue = this.notesData[key]

          return (
            notesDataValue !== "" &&
            notesDataValue !== null &&
            note[key] &&
            typeof note[key] === "string" &&
            note[key].includes(notesDataValue)
          )
        })

        return matchesCriteria
      })
    },
  },

  methods: {
    async loadNote(id) {
      this.is_loading = true
      try {
        await store.dispatch("getNote", id)
        if (this.note) {
          this.notesData = JSON.parse(JSON.stringify(this.note))
        } else {
          console.warn(`Note with ID ${id} not found.`)
          this.notesData = {
            id: null,
            title: "",
            issue_type: "",
            stop_id: "",
            route_id: "",
            vehicle_id: "",
            device_id: "",
            case_id: "",
            comments: "",
            priority: null,
            status: null,
            claimant: "",
            assigned: "",
            location: "",
            created: null,
            fault_category: null,
            noteUpdates: [],
          }
        }
      } catch (error) {
        console.error("Error loading note:", error)
        store.commit("SET_SNACKBAR", {
          text: `Failed to load note with ID ${id}.`,
          color: "error",
        })
        this.$router.push({ name: "next-stop-notes" })
      } finally {
        this.is_loading = false
      }
    },

    async addNoteUpdate() {
      if (!this.newUpdate.comments && !this.newUpdate.status && !this.newUpdate.assigned) {
        store.commit("SET_SNACKBAR", {
          text: `At least one field (Comment, Status, or Assigned To) must be filled to add an update.`,
          color: "warning",
        })

        return
      }

      try {
        const updatedNotePayload = JSON.parse(JSON.stringify(this.notesData))
        if (updatedNotePayload.updates) {
          delete updatedNotePayload.updates
        }
        this.newUpdate.updated_by = this.$auth.user.name
        updatedNotePayload.newUpdate = this.newUpdate

        await store.dispatch("updateNote", updatedNotePayload)

        // Update the main note's status and assignment fields on the frontend immediately
        this.notesData.status = updatedNotePayload.new_status
        this.notesData.assigned = updatedNotePayload.new_assigned

        // Reset the update form
        this.newUpdate = {
          comments: "",
          status: null,
          assigned: null,
          updated_by: null,
        }

        // Re-fetch the whole note data to display the new update in the history timeline
        this.loadNote(this.notesData.id)
      } catch (error) {
        console.error("Error adding note update:", error)
        store.commit("SET_SNACKBAR", {
          text: `Failed to add note update: ${error.message || "An unknown error occurred"}`,
          color: "error",
        })
      }
    },

    // New helper method for formatting update timestamps
    formatUpdateTimestamp(date) {
      return dayjs(date).format("YYYY-MM-DD HH:mm")
    },

    saveNoteConfig() {
      const payload = JSON.parse(JSON.stringify(this.notesData))

      if (payload.updates) {
        delete payload.updates
      }

      store.dispatch("updateNote", payload)
    },

    async fetchErrorLogs() {
      if (!this.notesData.device_id || !this.metricsRange.start || !this.metricsRange.end) {
        this.errorLogs = []

        return
      }

      this.errorLogsRequest.cancel("New fetch requested")
      this.errorLogsRequest = axios.CancelToken.source()
      this.errorLogsLoading = true

      try {
        const start = dayjs.tz(this.metricsRange.start, "Pacific/Auckland").format("YYYY-MM-DD HH:mm:ss")
        const end = dayjs.tz(this.metricsRange.end, "Pacific/Auckland").format("YYYY-MM-DD HH:mm:ss")

        const data = await store.dispatch("getDeviceEventsByVehicleId", {
          device_id: this.notesData.device_id,
          event_type: "error_logs",
          start_datetime: start,
          end_datetime: end,
          cancelToken: this.errorLogsRequest.token,
        })

        if (data && data.error) {
          throw new Error(data.error)
        }

        this.errorLogs = (data || []).map(i => ({
          type: i.event_type,
          ...i.event_data,
        }))
      } catch (e) {
        if (!axios.isCancel(e)) {
          console.error("Error fetching device error logs:", e)
          this.errorLogs = []
        }
      } finally {
        this.errorLogsLoading = false
      }
    },

    async fetchTripAssignments() {
      if (!this.notesData.device_id || !this.metricsRange.start || !this.metricsRange.end) {
        this.tripAssignments = []

        return
      }

      this.tripAssignmentsRequest.cancel("New fetch requested")
      this.tripAssignmentsRequest = axios.CancelToken.source()
      this.tripAssignmentsLoading = true

      try {
        const start = dayjs.tz(this.metricsRange.start, "Pacific/Auckland").format("YYYY-MM-DD HH:mm:ss")
        const end = dayjs.tz(this.metricsRange.end, "Pacific/Auckland").format("YYYY-MM-DD HH:mm:ss")

        const data = await store.dispatch("getDeviceEventsByVehicleId", {
          device_id: this.notesData.device_id,
          event_type: "trip_assignments",
          start_datetime: start,
          end_datetime: end,
          cancelToken: this.tripAssignmentsRequest.token,
        })

        if (data && data.error) {
          throw new Error(data.error)
        }

        this.tripAssignments = (data || []).map(i => ({
          type: i.event_type,
          ...i.event_data,
        }))
      } catch (e) {
        if (!axios.isCancel(e)) {
          console.error("Error fetching trip assignments:", e)
          this.tripAssignments = []
        }
      } finally {
        this.tripAssignmentsLoading = false
      }
    },

    async deleteNote() {
      try {
        await store.dispatch("removeNote", this.note.id)
        store.commit("SET_SNACKBAR", {
          text: `This note '${this.note.title}' was deleted`,
          color: "success",
        })

        setTimeout(() => {
          this.$router.push({
            name: "next-stop-notes",
          })
        }, 2000)
      } catch (error) {
        console.log("error", error)
        store.commit("SET_SNACKBAR", {
          text: `This note '${this.note.title}' could not be deleted`,
          color: "error",
        })
      }
    },
  },

  setup() {
    const curr = getCurrentInstance()
    const noteId = curr.proxy.$route.params.id

    if (!noteId) {
      curr.proxy.$router.push({
        name: "next-stop-notes",
      })
    }

    return {
      noteId,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "@/styles/next-stop/components/map.scss";

.mgl-map-wrapper {
  height: 15rem;
  border-radius: 20px;
  overflow: hidden;
}
</style>
